import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Progress,
  Table,
  Tag,
  Avatar,
  Space,
  Spin,
  Alert,
  Timeline,
  List
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  RiseOutlined,
  BankOutlined,
  SettingOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { getAllUsers, getAllClientsData, getManovre } from '../../apis';
import "../../styles/admin/admin-dashboard.css";
import "../../styles/admin/admin-tables.css";

const { Title, Text } = Typography;

function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    users: [],
    companies: [],
    manovre: [],
    stats: {
      totalUsers: 0,
      approvedUsers: 0,
      pendingUsers: 0,
      disabledUsers: 0,
      rejectedUsers: 0,
      totalCompanies: 0,
      totalManovre: 0
    }
  });

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [usersResponse, companiesResponse, manuvreResponse] = await Promise.allSettled([
        getAllUsers(),
        getAllClientsData(),
        getManovre()
      ]);

      const users = usersResponse.status === 'fulfilled' ? usersResponse.value || [] : [];
      const companies = companiesResponse.status === 'fulfilled' ? companiesResponse.value || [] : [];
      const manovre = manuvreResponse.status === 'fulfilled' ? manuvreResponse.value || [] : [];

      // Calculate statistics
      const stats = {
        totalUsers: users.length,
        approvedUsers: users.filter(user => user.status?.includes('approved')).length,
        pendingUsers: users.filter(user => user.status?.includes('pending')).length,
        disabledUsers: users.filter(user => user.status?.includes('disabled')).length,
        rejectedUsers: users.filter(user => user.status?.includes('rejected')).length,
        totalCompanies: companies.length,
        totalManovre: manovre.length
      };

      setDashboardData({
        users: users.slice(0, 5), // Latest 5 users for recent activity
        companies,
        manovre,
        stats
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getStatusColor = (status) => {
    const statusColors = {
      approved: '#52c41a',
      pending: '#faad14',
      disabled: '#ff4d4f',
      rejected: '#ff4d4f'
    };
    const cleanStatus = status?.replace('StatusEnum.', '') || 'pending';
    return statusColors[cleanStatus] || '#d9d9d9';
  };

  const getStatusIcon = (status) => {
    const cleanStatus = status?.replace('StatusEnum.', '') || 'pending';
    switch (cleanStatus) {
      case 'approved':
        return <CheckCircleOutlined />;
      case 'pending':
        return <ClockCircleOutlined />;
      case 'disabled':
      case 'rejected':
        return <CloseCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const recentUsersColumns = [
    {
      title: 'Utente',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.first_name} {record.last_name}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.email}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Azienda',
      dataIndex: 'company',
      key: 'company',
      render: (company) => (
        <Tag color="blue">{company}</Tag>
      )
    },
    {
      title: 'Stato',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const cleanStatus = status?.replace('StatusEnum.', '') || 'pending';
        return (
          <Tag 
            color={getStatusColor(status)} 
            icon={getStatusIcon(status)}
          >
            {cleanStatus.toUpperCase()}
          </Tag>
        );
      }
    }
  ];

  const approvalRate = dashboardData.stats.totalUsers > 0 
    ? Math.round((dashboardData.stats.approvedUsers / dashboardData.stats.totalUsers) * 100)
    : 0;

  if (loading) {
    return (
      <div className="admin-loading-container">
        <Spin size="large" className="admin-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <div className="dashboard-header">
        <Title level={2} className="dashboard-title">
          Dashboard Amministratore
        </Title>
      </div>

      {/* Main Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-body">
            <UserOutlined className="stat-icon" />
            <div className="stat-value">{dashboardData.stats.totalUsers}</div>
            <div className="stat-label">Utenti Totali</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-card-body">
            <BankOutlined className="stat-icon" />
            <div className="stat-value">{dashboardData.stats.totalCompanies}</div>
            <div className="stat-label">Aziende Registrate</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-card-body">
            <SettingOutlined className="stat-icon" />
            <div className="stat-value">{dashboardData.stats.totalManovre}</div>
            <div className="stat-label">Manovre Totali</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-card-body">
            <TrophyOutlined className="stat-icon" />
            <div className="stat-value">{approvalRate}%</div>
            <div className="stat-label">Tasso Approvazione</div>
          </div>
        </div>
      </div>

      {/* User Status Distribution */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title="Distribuzione Stati Utenti" bordered={false}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" className="status-tag-completed">
                  <Statistic
                    title="Approvati"
                    value={dashboardData.stats.approvedUsers}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" className="status-tag-pending">
                  <Statistic
                    title="In Attesa"
                    value={dashboardData.stats.pendingUsers}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" className="status-tag-rejected">
                  <Statistic
                    title="Disabilitati"
                    value={dashboardData.stats.disabledUsers}
                    prefix={<CloseCircleOutlined />}
                    valueStyle={{ fontSize: '18px' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" className="status-tag-rejected">
                  <Statistic
                    title="Rifiutati"
                    value={dashboardData.stats.rejectedUsers}
                    prefix={<WarningOutlined />}
                    valueStyle={{ fontSize: '18px' }}
                  />    
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Progressi Sistema" bordered={false}>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Utenti Approvati</Text>
              <Progress
                percent={approvalRate}
                strokeColor="#52c41a"
                style={{ marginTop: '8px' }}
                showInfo={true}
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Utenti in Attesa</Text>
              <Progress
                percent={dashboardData.stats.totalUsers > 0 
                  ? Math.round((dashboardData.stats.pendingUsers / dashboardData.stats.totalUsers) * 100)
                  : 0}
                strokeColor="#faad14"
                style={{ marginTop: '8px' }}
                showInfo={true}
              />
            </div>

            <div>
              <Text strong>Copertura Aziende</Text>
              <Progress
                percent={Math.min(dashboardData.stats.totalCompanies * 10, 100)} // Mock percentage
                strokeColor="#722ed1"
                style={{ marginTop: '8px' }}
                showInfo={true}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity and Top Companies */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Utenti Recenti" className="admin-table-container" bordered={false}>
            {dashboardData.users.length > 0 ? (
              <Table
                className="admin-data-table"
                columns={recentUsersColumns}
                dataSource={dashboardData.users.map((user, index) => ({ ...user, key: index }))}
                pagination={false}
                size="small"
              />
            ) : (
              <Alert
                message="Nessun utente trovato"
                description="Non ci sono utenti registrati nel sistema."
                type="info"
                showIcon
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Riepilogo Attività" bordered={false}>
            <Timeline>
              <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
                <Text strong>{dashboardData.stats.approvedUsers}</Text> utenti approvati
                <br />
                <Text type="secondary">Sistema operativo</Text>
              </Timeline.Item>
              
              <Timeline.Item color="blue" dot={<TeamOutlined />}>
                <Text strong>{dashboardData.stats.totalCompanies}</Text> aziende registrate
                <br />
                <Text type="secondary">Network in crescita</Text>
              </Timeline.Item>
              
              <Timeline.Item color="orange" dot={<ClockCircleOutlined />}>
                <Text strong>{dashboardData.stats.pendingUsers}</Text> richieste in attesa
                <br />
                <Text type="secondary">Richiedono revisione</Text>
              </Timeline.Item>
              
              <Timeline.Item color="purple" dot={<RiseOutlined />}>
                <Text strong>{dashboardData.stats.totalManovre}</Text> manovre configurate
                <br />
                <Text type="secondary">Sistema attivo</Text>
              </Timeline.Item>
            </Timeline>
          </Card>
        </Col>
      </Row>

      {/* System Alerts */}
      {dashboardData.stats.pendingUsers > 0 && (
        <Row style={{ marginTop: '24px' }}>
          <Col span={24}>
            <Alert
              message={`Attenzione: ${dashboardData.stats.pendingUsers} utenti in attesa di approvazione`}
              description="Ci sono utenti che richiedono la tua attenzione per l'approvazione."
              type="warning"
              showIcon
              action={
                <Text style={{ color: '#fa8c16', cursor: 'pointer' }} strong>
                  Visualizza →
                </Text>
              }
            />
          </Col>
        </Row>
      )}
    </div>
  );
}

export default AdminDashboard;